import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getRendersByProject } from "../../../../../services/rendersService";

const STATUS_CFG = {
  PENDING: { label: "Pending", bg: "#F3F4F6", color: "#374151" },
  SENT_TO_USER: { label: "Sent to Customer", bg: "#DBEAFE", color: "#1E40AF" },
  APPROVED: { label: "Approved", bg: "#D1FAE5", color: "#065F46" },
  REJECTED: { label: "Rejected", bg: "#FEE2E2", color: "#991B1B" },
  REQUESTED_FOR_CHANGE: {
    label: "Changes Requested",
    bg: "#FEF3C7",
    color: "#92400E",
  },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CFG[(status || "").toUpperCase()];
  if (!cfg) return null;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 12,
      }}
    />
  );
};

const PinMarker = ({ pin }) => (
  <Box
    sx={{
      position: "absolute",
      left: `${(pin.x_pos || 0) * 100}%`,
      top: `${(pin.y_pos || 0) * 100}%`,
      transform: "translate(-50%, -50%)",
      width: 22,
      height: 22,
      borderRadius: "50%",
      backgroundColor: "#4D6BF6",
      border: "2px solid rgba(255,255,255,0.85)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {pin.label || ""}
  </Box>
);

const RenderImageModal = ({ open, onClose, image }) => {
  if (!open || !image) return null;
  const url = image.url || image.path || image.image_thumbnail;
  return (
    <Modal open={open} onClose={onClose} disableAutoFocus>
      <Box
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        tabIndex={-1}
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ position: "relative", maxWidth: "92vw", maxHeight: "90vh" }}
        >
          <Box
            component="img"
            src={url}
            alt=""
            sx={{
              maxWidth: "92vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 1,
              display: "block",
            }}
          />
          {Array.isArray(image.pins) &&
            image.pins.map((p) => <PinMarker key={p.id} pin={p} />)}
        </Box>
      </Box>
    </Modal>
  );
};

const FloorPlanCard = ({ floorPlan, onClick }) => {
  const url = floorPlan.url || floorPlan.path || floorPlan.image_thumbnail;
  const pins = floorPlan.pins || [];
  return (
    <Box
      onClick={() => onClick?.(floorPlan)}
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid #E5E7EB",
        cursor: url ? "pointer" : "default",
        backgroundColor: "#F9FAFB",
        aspectRatio: "4 / 3",
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9CA3AF",
            fontSize: 13,
          }}
        >
          No floor plan
        </Box>
      )}
      {pins.length > 0 && (
        <Chip
          label={`${pins.length} viewpoint${pins.length === 1 ? "" : "s"}`}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.95)",
            color: "#0D0D0E",
            fontSize: 11,
            fontWeight: 600,
          }}
        />
      )}
      {floorPlan.status && (
        <Box sx={{ position: "absolute", bottom: 8, left: 8 }}>
          <StatusPill status={floorPlan.status} />
        </Box>
      )}
    </Box>
  );
};

const RoomBlock = ({ room, onImageClick }) => {
  const floorPlan = room.floor_plan || room.floorPlan;
  const pinRenders = (floorPlan?.pins || []).flatMap((p) =>
    (p.renders || []).map((r) => ({
      ...r,
      pin: p,
    }))
  );

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#fff",
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: "#0D0D0E",
          mb: 2,
        }}
      >
        {room.name || room.room_name || "Room"}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 12, color: "#9CA3AF", mb: 1, fontWeight: 500 }}
          >
            FLOOR PLAN
          </Typography>
          {floorPlan ? (
            <FloorPlanCard floorPlan={floorPlan} onClick={onImageClick} />
          ) : (
            <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
              No floor plan uploaded.
            </Typography>
          )}
        </Box>
        <Box>
          <Typography
            sx={{ fontSize: 12, color: "#9CA3AF", mb: 1, fontWeight: 500 }}
          >
            RENDERS ({pinRenders.length})
          </Typography>
          {pinRenders.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
              No renders.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                },
              }}
            >
              {pinRenders.map((r) => (
                <Box
                  key={r.id}
                  onClick={() => onImageClick?.(r)}
                  sx={{
                    aspectRatio: "4 / 3",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: "1px solid #F3F4F6",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="img"
                    src={r.url || r.path || r.image_thumbnail}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Renders = ({ projectId }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getRendersByProject(projectId);
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.rooms || [];
        setRooms(Array.isArray(list) ? list : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (rooms.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          border: "1px dashed #D1D5DB",
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0D0D0E" }}>
          No renders yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
          Floor plans and renders will appear here once added.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#0D0D0E", mb: 3 }}>
        Renders
      </Typography>
      {rooms.map((r) => (
        <RoomBlock
          key={r.id || r.room_id}
          room={r}
          onImageClick={(img) => setModalImage(img)}
        />
      ))}
      <RenderImageModal
        open={!!modalImage}
        image={modalImage}
        onClose={() => setModalImage(null)}
      />
    </Box>
  );
};

export default Renders;
